import { FC, useMemo, useState } from "react"
import { useQueryClient } from "react-query"
import { Formik, Form, FormikValues } from "formik"
import * as Yup from 'yup'
import {   
  StatusErrorMessage, 
  FormikCheckBox,
  FormikNotesArea
} from "../../../../../components/widgets"

import { doFormSubmit, isPopulatedArray, processNoteValues } from "../../../../../components/helpers"

import { cloneDeep, pickBy } from "lodash"
import { DLMROI, submitDLMPhase_3_1_4_4} from "../../../../core/doclabel"
import { SubmitButtonRow, VerdictSelector } from "../../../../../components/layout"

const initPendingInspectionValues = {
  verdict: "",
  note: '',
  noteAttachments: [],
}

const schema = Yup.object({
  verdict: Yup.string().trim(),
  note: Yup.string().trim().label('Note').required(),
  noteAttachments: Yup.array(),
})


type Props = {
  id: number
  state: string
  workflow: string
  appID: number
  rois: Array<DLMROI>
  doClose: Function
  hasVerdict?: boolean    
}

const SubmitNoteResponseForm: FC<Props> = ({id, state, workflow, appID, rois, doClose, hasVerdict = false}) => {
  const queryClient = useQueryClient();
  const [ verdict, setVerdict ] = useState<string | null>(null);

  const callFunction = useMemo(()=> {
    if (! state) return undefined;
    switch(state) {
      case 'Applicant Signed':
        return submitDLMPhase_3_1_4_4;
      default: 
        return undefined;
    }
  }, [state]);


  const initValues = useMemo(() => {
    return cloneDeep(initPendingInspectionValues);
  }, []);

  const doSubmit = async (values: any, actions: FormikValues) => {        
    
    const { setStatus, setSubmitting } = actions;

    if (callFunction === undefined) {
      setStatus('Call function undefined!');
      setSubmitting(false);
      return;
    }


    const payload = { 
      ...pickBy(values, (value, key) => (value && (! key.match(/(note|verdict|AttachmentName)$/i)))),
      siteApplicationid: id, 
    };

    processNoteValues(values, payload);
  
    if(values.verdict) {
      Object.assign(payload, { approved : values.verdict === 'approved'});
    }

    if (workflow === 'Label1b3' && state === 'Applicant Signed' && values.verdict === 'approved' && values?.skipSCDF) {
      // if skip is selected, need to send data similar to  3_1_4_2(ROI/DOC Pending SCDF Verification)
      Object.assign(payload, { roiModels: isPopulatedArray(rois) 
        ? rois.map( roi => ({ roiId: roi.id, roiAttachmentId: 0, roiAttachmentIdAttachmentName: '' }))
        : []
      });
    }
        
    await doFormSubmit(callFunction, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list' }]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      doClose();                
    });
  }

  return(
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status, setFieldValue, setStatus}) => (
    <Form id='kt_siteinspection_note_response_form' className='form' noValidate>
      <div className='d-flex flex-column p-2' > 
        { hasVerdict && 
        <VerdictSelector
            verdict={verdict}
            setVerdict={setVerdict}
            setFieldValue={setFieldValue}
            setStatus={setStatus}
        />
        }

        {((! hasVerdict) || verdict) && 
        <>
        { (['Label1b3','Label1b2'].includes(workflow) && state === 'Applicant Signed' && verdict === 'approved') && 
        <div className="row my-2">
          <FormikCheckBox size="12" name="skipSCDF" label="Skip SCDF" />  
        </div>
        }
    
        <FormikNotesArea required />
        
        <StatusErrorMessage status={status} />
        <SubmitButtonRow doClose={doClose} />
        </>
        }
      </div>
    </Form>
  )}
  </Formik>
  )
}

export { SubmitNoteResponseForm }